/**
 * Universal Dock - Shared types and constants
 */

import type { ReactNode } from 'react'

export interface DockApiClient {
  post: (url: string, data: any) => Promise<any>
  get: (url: string) => Promise<any>
}

export interface UniversalDockProps {
  /** Authenticated API client (same one used for GlobalSearch) */
  apiClient?: DockApiClient
  /** Current user info */
  user?: { id?: string; email?: string; name?: string } | null
  /** Organisation ID for Teams API */
  orgId?: string
  /** Hide the dock entirely */
  hidden?: boolean
  /** Custom page-specific action buttons rendered before the universal buttons */
  customActions?: DockAction[]
  /**
   * Additional registry-driven panels rendered alongside the built-in
   * Bug/Teams/AI trio. Each panel contributes its own toggle button
   * (right of AI) and slide-out body. See PR 3d (Stack 3).
   */
  customPanels?: DockPanel[]
  /** AI Hub URL to link to from the AI panel expand button */
  aiHubUrl?: string
}

export interface DockAction {
  id: string
  label: string
  icon: ReactNode
  onClick: () => void
  color?: 'teal' | 'blue' | 'amber' | 'purple' | 'rose'
}

/**
 * Registry-driven dock panel.
 *
 * A `DockPanel` contributes BOTH a toggle button in the dock bar AND a
 * slide-out body that renders when the button is active. The built-in
 * Bug Report / Teams Chat / AI Chat panels are themselves expressed as
 * DockPanel entries (`getBuiltinPanels()` in UniversalDock.tsx), so any
 * consumer-supplied panel is a first-class citizen with the same UX.
 *
 * USAGE — static (declared at app entry):
 *   <DashboardLayout
 *     bottomDockPanels={[{ id: 'notifications', label: 'Bell',
 *       icon: <Bell />, activeColor: 'amber',
 *       render: ({ isOpen, onClose }) =>
 *         <NotificationsPanel isOpen={isOpen} onClose={onClose} /> }]}
 *   />
 *
 * USAGE — dynamic (deep in the tree, page-scoped):
 *   const { setPanels } = useDockPanels()
 *   useEffect(() => {
 *     setPanels([{ id: 'inspector', ... }])
 *     return () => setPanels([])
 *   }, [])
 */
export interface DockPanel {
  /** Unique panel id (e.g. 'notifications', 'quick-settings') */
  id: string
  /** Button label shown in the dock bar */
  label: string
  /** Lucide icon (or any ReactNode) shown in the button */
  icon: ReactNode
  /**
   * Tailwind palette name for the active-state highlight on the button.
   * Defaults to 'teal' if omitted. Matches the built-in palette so
   * custom panels feel native to the dock.
   */
  activeColor?: 'rose' | 'violet' | 'teal' | 'amber' | 'blue' | 'purple'
  /**
   * Render the panel body. Receives `isOpen` (true when this panel is
   * the currently-active one in the dock) and `onClose` (a function
   * the body should call to dismiss itself, e.g. on backdrop click).
   * The body MUST handle its own `isOpen={false}` rendering (typically
   * `return null`); the dock does not do that for you.
   */
  render: (props: { isOpen: boolean; onClose: () => void }) => ReactNode
  /**
   * If true, hide the panel button label on small screens (`hidden
   * sm:inline`). Defaults to true to match built-in behavior.
   */
  hideLabelOnMobile?: boolean
}

// Admin API for bug reports
export const ADMIN_API_URL = 'https://0x3admin.quotech.ai'
export const BACKEND_API_URL = import.meta.env.VITE_API_URL || 'https://quotech-backend.quotech.ai'
